import React from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import DeleteIcon from "@mui/icons-material/Delete";
import { ImageFilters as IImageFilters } from "../../hooks/useImages";
import { useCategories, useDeleteCategory } from "../../hooks/useCategories";

interface ImageFiltersProps {
  filters: IImageFilters;
  onFiltersChange: (filters: IImageFilters) => void;
}

export const ImageFilters: React.FC<ImageFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const {
    data: categories,
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useCategories();
  const deleteCategoryMutation = useDeleteCategory();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({
      ...filters,
      search: e.target.value || undefined,
      page: 1, // Reset to page 1 on filter change
    });
  };

  const handleVisibilityChange = (newValue: boolean | undefined) => {
    onFiltersChange({
      ...filters,
      is_public: newValue,
      page: 1,
    });
  };

  const handlePageSizeChange = (e: any) => {
    onFiltersChange({
      ...filters,
      page_size: parseInt(e.target.value, 10),
      page: 1,
    });
  };

  const handleCategoryChange = (e: any) => {
    onFiltersChange({
      ...filters,
      category_id: e.target.value || undefined,
      page: 1,
    });
  };

  const handleDeleteCategory = async () => {
    if (!filters.category_id) return;

    const category = categories?.items.find(
      (cat) => cat.id === filters.category_id
    );
    if (!category) return;

    if (
      window.confirm(
        `Are you sure you want to delete the category "${category.name}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteCategoryMutation.mutateAsync(filters.category_id);
        // Category deleted successfully
        refetchCategories();
        onFiltersChange({
          ...filters,
          category_id: undefined,
          page: 1,
        });
      } catch (error) {
        // Failed to delete category
        console.error("Error deleting category:", error);
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 3,
        mb: 3,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {/* Search */}
      <TextField
        placeholder="Search images..."
        value={filters.search || ""}
        onChange={handleSearchChange}
        variant="standard"
        sx={{
          minWidth: { xs: "100%", sm: 300 },
          flex: { xs: "1 1 100%", sm: "0 1 auto" },
          "& .MuiInputBase-root": {
            fontSize: { xs: "16px", sm: "16px" },
            minHeight: { xs: "48px", sm: "40px" },
          },
          "& .MuiInputBase-input": {
            padding: { xs: "12px 14px", sm: "10px 14px" },
          },
          "& .MuiInput-underline:before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:hover:before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:after": {
            borderBottom: "none",
          },
        }}
      />

      {/* Category Filter */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: { xs: "100%", sm: "auto" } }}>
        <FormControl 
          variant="standard" 
          size="small" 
          sx={{ 
            minWidth: { xs: "100%", sm: 150 },
            flex: { xs: "1 1 100%", sm: "0 1 auto" },
            "& .MuiInputBase-root": {
              fontSize: { xs: "16px", sm: "16px" },
              minHeight: { xs: "48px", sm: "40px" },
            },
            "& .MuiInput-underline:before": {
              borderBottom: "none",
            },
            "& .MuiInput-underline:hover:before": {
              borderBottom: "none",
            },
            "& .MuiInput-underline:after": {
              borderBottom: "none",
            },
          }}
        >
          <InputLabel>Category</InputLabel>
          <Select
            value={filters.category_id || ""}
            label="Category"
            onChange={handleCategoryChange}
            disabled={categoriesLoading}
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {categories?.items.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filters.category_id && (
          <Tooltip title="Delete this category">
            <IconButton
              onClick={handleDeleteCategory}
              disabled={deleteCategoryMutation.isPending}
              size="small"
              sx={{
                color: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "error.dark",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Visibility Filter */}
      <Box sx={{ display: "flex", gap: { xs: 0.5, sm: 1 }, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
        <Button
          onClick={() => handleVisibilityChange(undefined)}
          variant="text"
          size="small"
          startIcon={<PublicIcon />}
          disableRipple
          sx={{
            color:
              filters.is_public === undefined
                ? "primary.main"
                : "text.secondary",
            fontWeight: filters.is_public === undefined ? 600 : 400,
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 1 },
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              color: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
              transform: "none",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
          }}
        >
          All
        </Button>
        <Button
          onClick={() => handleVisibilityChange(true)}
          variant="text"
          size="small"
          startIcon={<PublicIcon />}
          disableRipple
          sx={{
            color:
              filters.is_public === true ? "primary.main" : "text.secondary",
            fontWeight: filters.is_public === true ? 600 : 400,
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 1 },
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              color: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
              transform: "none",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
          }}
        >
          Public
        </Button>
        <Button
          onClick={() => handleVisibilityChange(false)}
          variant="text"
          size="small"
          startIcon={<LockIcon />}
          disableRipple
          sx={{
            color:
              filters.is_public === false ? "primary.main" : "text.secondary",
            fontWeight: filters.is_public === false ? 600 : 400,
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 1 },
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "transparent",
              color: "primary.main",
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
              transform: "none",
            },
            "&:focus": {
              backgroundColor: "transparent",
            },
          }}
        >
          Private
        </Button>
      </Box>

      {/* Page Size */}
      <FormControl 
        variant="standard" 
        size="small" 
        sx={{ 
          minWidth: { xs: "100%", sm: 120 },
          flex: { xs: "1 1 100%", sm: "0 1 auto" },
          "& .MuiInputBase-root": {
            fontSize: { xs: "16px", sm: "16px" },
            minHeight: { xs: "48px", sm: "40px" },
          },
          "& .MuiInput-underline:before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:hover:before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:after": {
            borderBottom: "none",
          },
        }}
      >
        <InputLabel>Per Page</InputLabel>
        <Select
          value={filters.page_size || 20}
          label="Per Page"
          onChange={handlePageSizeChange}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
      </FormControl>

      {/* Sort Buttons */}
      <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.5, sm: 1 }, flexWrap: "wrap", width: { xs: "100%", sm: "auto" } }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: "text.secondary", 
            mr: { xs: 0.5, sm: 1 },
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
          }}
        >
          Sort:
        </Typography>

        {/* Upload Date Sort */}
        <Button
          size="small"
          variant="text"
          disableRipple
          onClick={() => {
            const newOrder =
              filters.sort_by === "created_at" && filters.sort_order === "desc"
                ? "asc"
                : "desc";
            onFiltersChange({
              ...filters,
              sort_by: "created_at",
              sort_order: newOrder,
              page: 1,
            });
          }}
          sx={{
            minWidth: "auto",
            px: { xs: 1.5, sm: 1.5 },
            py: { xs: 1, sm: 0.5 },
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.75rem" },
            textTransform: "none",
            border: "none",
            boxShadow: "none",
            "&:focus": {
              boxShadow: "none",
            },
            "&:hover": {
              backgroundColor: "transparent",
              opacity: 0.7,
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
              transform: "none",
            },
          }}
        >
          Date{" "}
          <span
            style={{
              width: "12px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            {filters.sort_by === "created_at"
              ? filters.sort_order === "desc"
                ? "↓"
                : "↑"
              : ""}
          </span>
        </Button>

        {/* Title Sort */}
        <Button
          size="small"
          variant="text"
          disableRipple
          onClick={() => {
            const newOrder =
              filters.sort_by === "title" && filters.sort_order === "asc"
                ? "desc"
                : "asc";
            onFiltersChange({
              ...filters,
              sort_by: "title",
              sort_order: newOrder,
              page: 1,
            });
          }}
          sx={{
            minWidth: "auto",
            px: { xs: 1.5, sm: 1.5 },
            py: { xs: 1, sm: 0.5 },
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.75rem" },
            textTransform: "none",
            border: "none",
            boxShadow: "none",
            "&:focus": {
              boxShadow: "none",
            },
            "&:hover": {
              backgroundColor: "transparent",
              opacity: 0.7,
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
              transform: "none",
            },
          }}
        >
          Title{" "}
          <span
            style={{
              width: "12px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            {filters.sort_by === "title"
              ? filters.sort_order === "asc"
                ? "↑"
                : "↓"
              : ""}
          </span>
        </Button>

        {/* File Size Sort */}
        <Button
          size="small"
          variant="text"
          disableRipple
          onClick={() => {
            const newOrder =
              filters.sort_by === "file_size" && filters.sort_order === "desc"
                ? "asc"
                : "desc";
            onFiltersChange({
              ...filters,
              sort_by: "file_size",
              sort_order: newOrder,
              page: 1,
            });
          }}
          sx={{
            minWidth: "auto",
            px: { xs: 1.5, sm: 1.5 },
            py: { xs: 1, sm: 0.5 },
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.75rem" },
            textTransform: "none",
            border: "none",
            boxShadow: "none",
            "&:focus": {
              boxShadow: "none",
            },
            "&:hover": {
              backgroundColor: "transparent",
              opacity: 0.7,
            },
            "&:active": {
              backgroundColor: "transparent",
              boxShadow: "none",
              transform: "none",
            },
          }}
        >
          Size{" "}
          <span
            style={{
              width: "12px",
              display: "inline-block",
              textAlign: "center",
            }}
          >
            {filters.sort_by === "file_size"
              ? filters.sort_order === "desc"
                ? "↓"
                : "↑"
              : ""}
          </span>
        </Button>
      </Box>
    </Box>
  );
};
